import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, List, ListItem, Typography } from '@mui/material';
import { SUBSCRIPTION_PLAN } from 'constants/paths';
import { useGetOnePageStyleButtons } from 'layouts/OnePagePreviewLayout/hooks/useGetOnePageStyleButtons';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan } from 'store/useBusinessToolStore/selectors';

import { BottomSliderControlled } from 'components/_basic/BottomSlider';
import { Button } from 'components/_basic/Button/Button';

import { OnePageStyleSoon, OpenStyleSelectorButton, SelectOnePageStyleButton } from './components';
import { useUpdateOnePageStyle } from '../../hooks/useUpdateOnePageStyle';

import styles from './OnePageStyleSelector.module.scss';

export const OnePageStyleSelectorMobile: FC = () => {
  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: onePageButtons } = useGetOnePageStyleButtons();
  const { styleChangeHandler, isPending } = useUpdateOnePageStyle();

  const businessPlanOnePageStyle = businessPlan.metadata.onePageTemplateStyle;

  const [selectedStyle, setSelectedStyle] = useState(businessPlanOnePageStyle);

  const isAccessed = onePageButtons.find(({ style }) => style === selectedStyle)?.isAccessed;
  const isCurrentStyle = selectedStyle === businessPlanOnePageStyle;

  const upgradePlanHandler = () => navigate(SUBSCRIPTION_PLAN);

  return (
    <BottomSliderControlled
      renderController={(onClick) => (
        <OpenStyleSelectorButton
          onClick={onClick}
          businessPlanOnePageStyle={businessPlanOnePageStyle}
          className={styles['select-button-mobile']}
        />
      )}
    >
      {({ onClose }) => (
        <Box>
          <Typography variant="h5">{t('selectStyle')}</Typography>
          <List className={styles['menu-wrapper-mobile']}>
            {onePageButtons.map(({ style, bgImage, isAccessed }) => {
              const isSelected = style === selectedStyle;

              return (
                <ListItem className={styles['menu-item']} key={style}>
                  <SelectOnePageStyleButton
                    bgImage={bgImage}
                    isSelected={isSelected}
                    isAccessed={isAccessed}
                    onClick={() => setSelectedStyle(style)}
                  />
                </ListItem>
              );
            })}
            <ListItem className={styles['menu-item']}>
              <OnePageStyleSoon />
            </ListItem>
          </List>

          {isAccessed ? (
            <Button
              className={styles['apply-button']}
              variant="contained"
              disabled={isCurrentStyle || isPending}
              fullWidth
              isLoading={isPending}
              onClick={() => styleChangeHandler(selectedStyle, onClose)}
            >
              {t('apply')}
            </Button>
          ) : (
            <Button
              className={styles['apply-button']}
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => {
                upgradePlanHandler();
                onClose();
              }}
            >
              {t('upgradePlan')}
            </Button>
          )}
        </Box>
      )}
    </BottomSliderControlled>
  );
};
